import { useEffect, useRef } from "react";
import { animate, motion, useMotionValue } from "motion/react";
import { readFragment, ResultOf } from "gql.tada";
import { SlideCarouselSectionFragment } from "@queries/fragments/SlideCarouselSectionFragment";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { Link } from "@components/shared/core/Link";
import { Button } from "@components/shared/core/Button";
import { animationCurves } from "@utils/animationCurves";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, Document as RichTextDocument } from "@contentful/rich-text-types";

type SlideCarouselData = ResultOf<typeof SlideCarouselSectionFragment>;
type SlideItem = NonNullable<
  NonNullable<SlideCarouselData["itemsCollection"]>["items"][number]
>;

// News-reel style card (homepage). Image/video hero + category/title/body/CTA.
// Renders a direction-aware fill bar + clip-path category reveal when active.
export const NewsReelCard = ({
  item,
  index,
  totalItems,
  isActive,
  direction,
  onMouseEnter,
  onMouseLeave,
  slideRef,
}: {
  item: SlideItem;
  index: number;
  totalItems: number;
  isActive: boolean;
  direction: "left" | "right";
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  slideRef: (node: HTMLDivElement | null) => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Horizontal fill bar: scaleX 0→1, origin switches based on travel direction
  const barScaleX = useMotionValue(0);
  const barOrigin = useMotionValue("left");

  useEffect(() => {
    // Entry: fill enters from the side the user came from (opposite of travel direction)	
    //   going right → new card fill enters from the left	
    //   going left  → new card fill enters from the right	
    // Exit: fill chases toward the travel direction	
    //   going right → old card fill exits to the right	
    //   going left  → old card fill exits to the left
    const origin = isActive
      ? direction === "right"
        ? "left"
        : "right"
      : direction === "right"
      ? "right"
      : "left";
    barOrigin.set(origin);
    animate(barScaleX, isActive ? 1 : 0, animationCurves.macro);
  }, [isActive, direction, barScaleX, barOrigin]);

  // Category text reveal: clip-path synced with fill direction	
  const clipPath = useMotionValue("polygon(0 0, 0% 0, 0% 100%, 0 100%)");
  useEffect(() => {
    return barScaleX.on("change", (s) => {
      const origin = barOrigin.get();
      clipPath.set(
        origin === "left"
          ? `polygon(0 0, ${s * 100}% 0, ${s * 100}% 100%, 0 100%)`
          : `polygon(${(1 - s) * 100}% 0, 100% 0, 100% 100%, ${(1 - s) * 100}% 100%)`
      );
    });
  }, [barScaleX, barOrigin, clipPath]);

  // Video play/pause driven by active state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isActive) {
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [isActive]);

  const ctaLink = readFragment(LinkFragment, item.ctaLink);
  const videoLink = item.videoLink
    ? readFragment(LinkFragment, item.videoLink)
    : null;
  const imageIsVideo = item.image?.contentType?.startsWith("video/");
  const imageUrl = imageIsVideo ? undefined : item.image?.url;
  const mediaVideoUrl = imageIsVideo ? item.image?.url : videoLink?.url;
  const hasVideo = Boolean(mediaVideoUrl);

  return (
    <div
      ref={slideRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="snap-start shrink-0 w-[72vw] sm:w-[27rem] lg:w-[29rem] xl:w-[31rem] rounded-[0.75rem] bg-neutral-light/20 overflow-hidden select-none break-normal"
    >
      <article
        role="group"
        aria-roledescription="slide"
        aria-label={`Slide ${index + 1} of ${totalItems}`}
        className="h-full flex flex-col"
      >
        {/* Image / video — no fill overlay here */}	
        <div className="relative aspect-[16/10] overflow-hidden bg-brand-black">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={item.image?.title ?? item.title ?? "News visual"}
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
          {hasVideo && mediaVideoUrl && (
            <video
              ref={videoRef}
              src={mediaVideoUrl}
              className="absolute inset-0 h-full w-full object-cover"
              muted
              loop
              playsInline
              preload="auto"
              poster={imageUrl ?? undefined}
            />
          )}
        </div>

        {/* Content area — red fill sweeps directionally on hover/active */}
        <div className="flex flex-col flex-1 p-5 dsk:p-6 relative overflow-hidden">
          {/* Fill bar */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              scaleX: barScaleX,
              transformOrigin: barOrigin,
              backgroundColor:
                "rgb(var(--palette-current-deep) / var(--tw-bg-opacity, 1))",
            }}
          />

          {/* Content above the fill */}
          <div className="relative z-10 flex flex-col flex-1">
            {/* Category: red by default, white revealed by fill */}
            <div className="relative">
              <p className="text-label uppercase tracking-[0.12em] text-brand-red">
                {item.category}
              </p>
              <motion.p
                className="text-label uppercase tracking-[0.12em] text-brand-cream absolute inset-0 select-none"
                style={{ clipPath }}
                aria-hidden
              >
                {item.category}
              </motion.p>
            </div>

            <h3 className="text-heading-4 mt-3 text-balance text-brand-cream">
              {item.title}
            </h3>
            <p className="text-body-small text-brand-cream/75 mt-4 max-w-[52ch]">
              {!!item.description?.json && documentToReactComponents(
                item.description.json as RichTextDocument,
                { renderNode: { [BLOCKS.PARAGRAPH]: (_, children) => <span>{children}</span> } }
              )}
            </p>

            <div className="mt-auto pt-6 flex items-center justify-between gap-4">
              {item.advertisingTopic ? (
                <span className="text-label text-brand-cream/50">
                  {item.advertisingTopic}
                </span>
              ) : (
                <span />
              )}
              {ctaLink && (
                <Button
                  asChild
                  variant="light-on-blur"
                  size="small"
                  className="shrink-0"
                >
                  <Link link={ctaLink}>{ctaLink.text ?? "Learn more"}</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};
